import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider, Button, SvgIcon } from '@mui/material';
import { Form, Col, Row, Container } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { sub } from 'date-fns';
import InputTheme from '../Quoters/Common/Helpers/InputTheme';
import HeroImage from '../LandingPages/HeroImage/HeroImage';
import { TextInput, SelectInput, StyledRadioInput } from '../Helpers/Inputs';
import { InputMaskDate } from '../Helpers/Formatters';
import { object, string, date } from '../Helpers/Validators/customValidators';
import { getBasicInfo, setBasicInfo } from '../../sessionStorage';
import About from './About/About';
import GeneralInformation from './GeneralInformation/GeneralInformation';
import Marketing from './Marketing/Marketing';
import States from '../Helpers/Lists/States';
import ShieldCheckmarkIcon from '../../icons/shield-checkmark.svg?react'
import DeathBenefitIcon from '../../icons/deathBenefit.svg?react'
import CalendarScheduleIcon from '../../icons/calendarSchedule.svg?react';
import CallToAction from './CallToAction/CallToAction';
import './Landing.scss';

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location && location.search && location.search.toLowerCase().includes('prefill');
  const [formState, setFormState] = useState(Landing.DefaultFormState(prefill, getBasicInfo()));

  const formMethods = useForm({
    resolver: yupResolver(Landing.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState,
  });

  const { handleSubmit } = formMethods;

  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value });
  };

  const handleGenderValueChange = (field, value) => {
    handleValueChange(field, value);
  };

  const onSubmit = (s) => {
    setBasicInfo({ ...getBasicInfo(), ...formState });
    navigate('/policy-review/existing-insurance');
  };

  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  const heroImageUrl = "/product-landing/revi/hero-image.jpg";
  const heroImageTabletUrl = '/product-landing/revi/hero-image.jpg';
  const heroImageMobileUrl = '/product-landing/revi/hero-small.jpg';

  const form = <ThemeProvider theme={(theme) => createTheme({
    ...theme,
    ...InputTheme(),
  })}>
    <div className="basic-info-about-me d-flex justify-content-center">
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="justify-content-center" >
            <Col lg={12} className='w-100'>

              <StyledRadioInput
                name="gender"
                id="gender"
                value={formState.gender}
                options={[{ value: "F", label: "Female", icon: 'female' }, { value: "M", label: "Male", icon: 'male' }]}
                onChange={e => handleGenderValueChange('gender', e)}
                isBlankIcon={true}
                isBlankCheckMark={true}
              />

              <div className='mb-3'>
                <TextInput
                  name="birthdate"
                  id="birthdate"
                  label="Date of Birth"
                  value={formState.birthdate}
                  onChange={(e) => handleValueChange('birthdate', e.target.value)}
                  InputProps={{
                    inputComponent: InputMaskDate,
                  }}
                />
              </div>

              <div className='mb-3'>
                <SelectInput
                  name="state"
                  id="state"
                  value={formState.state}
                  onChange={(e) => handleValueChange('state', e.target.value)}
                  label="State"
                  selectOptions={States()}
                />
              </div>

            </Col>
          </Row>
          <Row className="mt-0">

            <Col className="text-end pb-0">
              <Button variant="contained" size="medium" color="primary" type="submit" fullWidth>
                <ShieldCheckmarkIcon className="button-icon" /> NEXT
              </Button>
            </Col>

          </Row>
        </Form>
      </FormProvider>
    </div>
  </ThemeProvider>

  return (
    <Container fluid>
      <HeroImage
        title={'Life Insurance Policy Review'}
        subtitle={'It May Be Time for a Check Up'}
        smallHero={true}
        heroImageUrl={heroImageUrl}
        heroImageTabletUrl={heroImageTabletUrl}
        heroImageMobileUrl={heroImageMobileUrl}
        form={form}
      />

      <About />

      <GeneralInformation />

      <CallToAction
        title="Get your policy review today"
        actions={<Button color="primary" onClick={scrollToTop}>SCHEDULE A REVIEW</Button>} />

      <Marketing />

      <CallToAction
        title="Get a personalized insurance quote today"
        subtitle="A great rate is just a few clicks away."
        actions={
          <div className="button-container d-flex px-0">
            <Button className="button-block" color="primary" href="/landing/term">
              <div className="icon"><SvgIcon component={CalendarScheduleIcon} color='primary' /></div>TERM LIFE<br />INSURANCE</Button>
            <Button className="button-block" color="primary" href="/landing/permanent">
              <div className="icon"><SvgIcon component={DeathBenefitIcon} color='primary' /></div>SIMPLIFIED ISSUE<br />WHOLE LIFE INSURANCE</Button>
          </div>
        } />
    </Container>
  )
}

Landing.ValidationSchema = object().shape({
  gender: string().required('Gender is required.'),
  birthdate: date()
    .typeError('Invalid date.')
    .max(sub(new Date(), { days: 0 }), 'Future Date is not allowed.')
    .required('Birthdate is required.'),
  state: string().required('State is required.'),
});

Landing.DefaultFormState = (prefill = false, basicInfo) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender || ''),
    birthdate: prefill ? '10/10/1990' : (basicInfo && basicInfo.birthdate || ''),
    state: prefill ? 'ID' : (basicInfo && basicInfo.state || ''),
  };
};
